import { useRef, useState, useLayoutEffect, useCallback } from "react";
import cn from "mcn";
import { useGridSettingsWidth } from "../../../grid-settings/hooks";
import { CloseIcon } from "../../../icons/CloseIcon";

export const FormatModalWrapper = ({
  children,
  title,
  constrainRef,
  onClose,
}: {
  children: React.ReactNode;
  title: string;
  constrainRef: React.RefObject<HTMLDivElement>;
  onClose: () => void;
}) => {
  "use no memo";

  const { dragging, ref: dragRef, start: dragStart } = useDraggable({ constrainRef, position: "center" });
  const left = useGridSettingsWidth();

  return (
    <div
      ref={dragRef}
      data-testid="modal"
      className={cn(
        "rounded-lg border outline-outline overflow-hidden select-none",
        "z-[30] bg-card absolute grid grid-rows-[min-content_1fr] max-h-full max-w-full w-[520px]",
        "transition-[opacity,transform,box-shadow] duration-100 [&_input]:select-text [&_textarea]:select-text",
        [dragging, "opacity-90"],
        [
          dragging,
          "dark:shadow-[5px_3px_7px_1px_rgba(100,100,100,0.25)] shadow-[5px_3px_7px_1px_rgba(0,0,0,0.25)]",
          "dark:shadow-[4px_2px_6px_-1px_rgba(100,100,100,0.25)] shadow-[4px_2px_6px_-1px_rgba(0,0,0,0.25)]",
        ],
      )}
      style={{
        transform: `translateX(${left}px)`,
      }}
    >
      <div
        className={cn(
          "flex w-full text-foreground items-center pt-[var(--spacing-scale-medium-500,15px)] px-[var(--spacing-scale-medium-500,15px)]",
          dragging ? "cursor-grabbing" : "cursor-grab",
        )}
        onMouseDown={dragStart}
        onTouchStart={dragStart}
      >
        <span className="flex-1 text-[13.5px] font-[var(--font-weights-semibold)]">{title}</span>
        <CloseIcon onClick={onClose} onMouseDown={(e) => e.stopPropagation()} />
      </div>
      {children}
    </div>
  );
};

const useDraggable = ({
  constrainRef,
  position,
}: {
  constrainRef: React.RefObject<HTMLDivElement>;
  position?: "center";
}) => {
  const elementRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);
  const stateRef = useRef({ x: 0, y: 0 });
  const offsetRef = useRef({ x: 0, y: 0 });
  // Position stored as a fraction of the available drag space
  // (parent size minus modal size). When the parent resizes we re-derive
  // pixel offsets from this ratio so the modal slides proportionally
  // instead of staying pinned until it bumps a parent edge.
  const ratioRef = useRef<{ x: number; y: number } | null>(null);

  const start = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    setDragging(true);
    const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
    const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;
    const parentRect = constrainRef.current?.getBoundingClientRect();
    if (!parentRect) return;
    stateRef.current = {
      x: clientX - offsetRef.current.x - parentRect.left,
      y: clientY - offsetRef.current.y - parentRect.top,
    };
  };

  const move = useCallback(() => {
    if (!elementRef) return;
    const bounding = constrainRef.current?.getBoundingClientRect();
    if (elementRef.current !== null) {
      elementRef.current.style.left = `${(bounding?.left ?? 0) + offsetRef.current.x}px`;
      elementRef.current.style.top = `${(bounding?.top ?? 0) + offsetRef.current.y}px`;
    }
  }, [constrainRef]);

  const update = useCallback(
    (x: number, y: number) => {
      offsetRef.current = { x: x, y: y };
      move();
    },
    [move],
  );

  const updateAndRememberRatio = useCallback(
    (x: number, y: number) => {
      if (constrainRef.current && elementRef.current) {
        const parentRect = constrainRef.current.getBoundingClientRect();
        const modalRect = elementRef.current.getBoundingClientRect();
        const availX = Math.max(0, parentRect.width - modalRect.width);
        const availY = Math.max(0, parentRect.height - modalRect.height);
        ratioRef.current = {
          x: availX > 0 ? x / availX : 0.5,
          y: availY > 0 ? y / availY : 0.5,
        };
      }
      update(x, y);
    },
    [constrainRef, update],
  );

  useLayoutEffect(() => {
    if (!dragging) return;

    const onMove = (e: MouseEvent | TouchEvent) => {
      const pos = isTouchEvent(e)
        ? { x: e.touches[0].clientX, y: e.touches[0].clientY }
        : { x: e.clientX, y: e.clientY };

      if (elementRef.current !== null) {
        const parentRect = constrainRef.current?.getBoundingClientRect();
        const modalRect = elementRef.current.getBoundingClientRect();
        if (!parentRect || !modalRect) return;

        let newX = pos.x - stateRef.current.x - parentRect.left;
        let newY = pos.y - stateRef.current.y - parentRect.top;

        newX = Math.max(0, Math.min(newX, parentRect.width - modalRect.width));
        newY = Math.max(0, Math.min(newY, parentRect.height - modalRect.height));

        updateAndRememberRatio(newX, newY);
      }
    };

    const onUp = () => setDragging(false);

    window.addEventListener("mousemove", onMove, { passive: true });
    window.addEventListener("mouseup", onUp, { passive: true });
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", onUp, { passive: true });
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, [dragging, updateAndRememberRatio, constrainRef]);

  useLayoutEffect(() => {
    if (elementRef.current == null || !constrainRef.current || !position) return;
    const parentRect = constrainRef.current.getBoundingClientRect();
    const modalRect = elementRef.current.getBoundingClientRect();
    updateAndRememberRatio((parentRect.width - modalRect.width) / 2, (parentRect.height - modalRect.height) / 2);
  }, [position, constrainRef, updateAndRememberRatio]);

  useLayoutEffect(() => {
    const parent = constrainRef.current;
    if (!parent) return;
    const observer = new ResizeObserver(() => {
      if (elementRef.current === null || ratioRef.current === null) return;
      const parentRect = parent.getBoundingClientRect();
      const modalRect = elementRef.current.getBoundingClientRect();
      const availX = Math.max(0, parentRect.width - modalRect.width);
      const availY = Math.max(0, parentRect.height - modalRect.height);
      update(ratioRef.current.x * availX, ratioRef.current.y * availY);
    });
    observer.observe(parent);
    return () => observer.disconnect();
  }, [constrainRef, update]);

  return { dragging, ref: elementRef, start };
};

const isTouchEvent = (e: MouseEvent | TouchEvent): e is TouchEvent => "touches" in e;
